/**
 * "Use your own agent" on the agent page, `/agent` (AgentPage.tsx,
 * docs/agents.md; it was a section of the paid task board until that retired
 * on 2026-10-31): the address of the EngineerOS MCP server, the line that
 * adds it to Claude Code, and the learner's agent keys, made, listed and
 * revoked with their session.
 *
 * A key is shown once, when it is made: the Worker keeps only its hash.
 */
import { useCallback, useEffect, useState } from 'react';
import { useStrings } from '../../state/useLocale';
import { chatUrl } from '../../lib/chat';
import { reportSettings } from '../../lib/agent-keys';

interface AgentKey {
  id: string;
  name: string;
  prefix: string;
  createdAt: number;
  lastUsedAt: number | null;
  revokedAt: number | null;
}

const MAX_NAME = 60;

export async function agentCall(path: string, body?: unknown): Promise<{ status: number; data: Record<string, unknown> | null }> {
  try {
    const r = await fetch(chatUrl(path).href, {
      method: body === undefined ? 'GET' : 'POST',
      credentials: 'include',
      cache: 'no-store',
      ...(body === undefined ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    });
    return { status: r.status, data: await r.json().catch(() => null) };
  } catch {
    return { status: 0, data: null };
  }
}

export function Copy({ text }: { text: string }) {
  const s = useStrings().agents;
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-quiet"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => setDone(true), () => undefined);
      }}
    >
      {done ? s.copied : s.copy}
    </button>
  );
}

export function AgentKeys({ signedIn, onSignIn }: { signedIn: boolean; onSignIn(): void }) {
  const s = useStrings().agents;
  const [keys, setKeys] = useState<AgentKey[] | null>(null);
  const [name, setName] = useState('');
  const [fresh, setFresh] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const server = new URL('/api/mcp', window.location.origin).href;
  const addLine = `claude mcp add --transport http engineeros ${server} --header "Authorization: Bearer ${fresh ?? '<your key>'}"`;
  // The same key has Claude Code report its own model calls, so the level can
  // show what the agent spent (worker/ai-telemetry.ts).
  const measure = reportSettings(window.location.origin, fresh ?? '<your key>');

  const load = useCallback(async () => {
    const { status, data } = await agentCall('/api/agent/keys');
    if (status === 200 && Array.isArray(data?.keys)) setKeys(data.keys as AgentKey[]);
  }, []);
  useEffect(() => {
    if (signedIn) void load();
  }, [signedIn, load]);

  const make = async () => {
    setBusy(true);
    setProblem(null);
    const { status, data } = await agentCall('/api/agent/keys', { name: name.trim() });
    setBusy(false);
    if (status === 201 && typeof data?.secret === 'string') {
      setFresh(data.secret);
      setName('');
      void load();
    } else if (status === 409) {
      setProblem(s.tooMany(Number(data?.max) || 5));
    } else {
      setProblem(s.failed);
    }
  };

  const revoke = async (id: string) => {
    const { status } = await agentCall(`/api/agent/keys/${id}/revoke`, {});
    if (status === 200) void load();
    else setProblem(s.failed);
  };

  return (
    <section className="entry agent-keys" id="agent" aria-labelledby="agent-keys-title">
      <h2 className="projects__h2" id="agent-keys-title">{s.title}</h2>
      <p>{s.intro}</p>
      <p className="entry__hint">{s.limits}</p>

      <dl className="agent-keys__how">
        <dt>{s.server}</dt>
        <dd><code dir="ltr">{server}</code> <Copy text={server} /></dd>
        <dt>{s.forClaude}</dt>
        <dd><code dir="ltr" className="agent-keys__line">{addLine}</code> <Copy text={addLine} /></dd>
        <dt>{s.measure}</dt>
        <dd>
          <p className="entry__hint">{s.measureHint}</p>
          <pre dir="ltr" className="aikey__code"><code>{measure}</code></pre> <Copy text={measure} />
        </dd>
      </dl>

      {!signedIn ? (
        <button type="button" className="entry__cta" onClick={onSignIn}>{s.signIn}</button>
      ) : (
        <>
          {fresh && (
            <p className="entry__state" role="status">
              {s.shownOnce} <code dir="ltr">{fresh}</code> <Copy text={fresh} />
            </p>
          )}
          <form
            className="deliver agent-keys__form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!busy && name.trim()) void make();
            }}
          >
            <label className="deliver__field">
              <span className="deliver__hint">{s.nameLabel}</span>
              <input value={name} maxLength={MAX_NAME} placeholder={s.namePlaceholder} onChange={(e) => setName(e.target.value)} />
            </label>
            <div className="deliver__acts">
              <button type="submit" className="entry__cta" disabled={busy || name.trim() === ''}>{s.make}</button>
            </div>
          </form>
          {problem && <p className="entry__state" role="alert">{problem}</p>}
          {keys && keys.length === 0 && <p className="entry__hint">{s.none}</p>}
          {keys && keys.length > 0 && (
            <ul className="agent-keys__list">
              {keys.map((k) => (
                <li key={k.id} className={k.revokedAt ? 'is-revoked' : undefined}>
                  <strong>{k.name}</strong> <code dir="ltr">{k.prefix}…</code>
                  <span className="entry__hint">
                    {' '}{s.made(k.createdAt)} · {k.revokedAt ? s.revoked(k.revokedAt) : k.lastUsedAt ? s.lastUsed(k.lastUsedAt) : s.neverUsed}
                  </span>
                  {!k.revokedAt && (
                    <button type="button" className="btn btn-quiet" onClick={() => void revoke(k.id)}>{s.revoke}</button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
